import { PageLayout } from '../layout/PageLayout'
import heroRabobankLandingImage from '@app/assets/images/rabobank-landing/hero.png'
import rabobankLogo from '@app/assets/images/rabobank-landing/rabobank-logo.png'
import peopleBuildingImage from '@app/assets/images/rabobank-landing/people-building.jpg'
import peopleInRoomImage from '@app/assets/images/rabobank-landing/people-in-the-room.jpg'
import useMediaQuery from '@app/util/useMediaQuery'
import { Style } from 'inlines'
import { useMemo } from 'react'
import { Hero } from '@app/components/blocks/hero/Hero'
import { ColumnsSection } from '@app/components/blocks/contentSection/ColumnsSection'
import { TextLink } from '@app/components/core/link/TextLink'
import { Text } from '@app/components/core/typography/Text'
import { Title } from '@app/components/core/typography/Title'
import { CpoDesks } from './blocks/CpoDesks'

export const RabobankLanding = () => {
  const isMobile = useMediaQuery()

  const imageStyle: Style = useMemo(
    () => ({
      height: isMobile ? 'auto' : '20rem',
      width: isMobile ? '100%' : '31rem',
      aspectRatio: '1 / 1',
      objectFit: 'cover',
    }),
    [isMobile],
  )

  const logoStyle: Style = useMemo(
    () => ({
      width: isMobile ? '100%' : 'auto',
      maxWidth: '31rem',
      height: 'auto',
      objectFit: 'contain',
    }),
    [isMobile],
  )

  return (
    <>
      <PageLayout gap={isMobile ? '2.5rem' : '6.25rem'} marginTop="0">
        <Hero 
            title="CPO-desk in jouw regio" 
            subtitle="Van plan om samen met een groep een CPO-project te starten? Dan helpt Rabobank je graag verder." 
            backgroundSrc={heroRabobankLandingImage} 
            fullWidth 
            centered
        /> 
        
        <ColumnsSection
          style={{
            alignItems: 'center',
            flexDirection: isMobile ? 'column-reverse' : 'row',
          }}
          columns={[
            {
              imageSrc: rabobankLogo,
              imageAlt: 'Rabobank logo',
              imageStyle: {
                ...logoStyle,
                marginRight: 'auto',
              },
            },
            {
              markdown: `## Maak kennis met de Rabobank CPO-desk.\n
Of je nu in de opstartfase zit of al op weg bent. De gespecialiseerde 
adviseurs van de Rabobank, zetten hun kennis, kunde én netwerk in 
om vragen rondom Samen Zelfbouwen te beantwoorden. Ze wijzen de 
weg, geven advies én kunnen uiteraard helpen bij de financiering als 
het zover is. Ook biedt Rabobank gratis vergaderruimtes in haar 
kantoren aan voor CPO - groepen, inclusief koffie en thee.
          `,
            },
            
          ]}
        />

        <ColumnsSection
          style={{
            alignItems: 'center',
            flexDirection: isMobile ? 'column-reverse' : 'row',
          }}
          columns={[
            {
              markdown: `## Collectief Particulier Opdrachtgeverschap\n
Je eigen thuis bouwen, met al jouw wensen en ideeën. En dan ook 
nog samen met anderen. Er komt geen projectontwikkelaar aan te 
pas. Als collectief kies je alles zelf: architect, aannemer, bouwstijl, 
mate van duurzaamheid, wel of geen gemeenschappelijke ruimte. Je 
bouwt tegen kostprijs. En als bewoner bepaal je zelf waar de muren, 
keuken en badkamer in jouw eigen woning komen. Het is allemaal 
mogelijk in Collectief Particulier Opdrachtgeverschap (CPO).
          `,
            },
            {
              imageSrc: peopleBuildingImage,
              imageAlt: 'people building',
              imageStyle: {
                  ...imageStyle,
                  marginLeft: 'auto',
                },
            },

          ]}
        />

        <ColumnsSection
          style={{
            alignItems: 'center',
            flexDirection: isMobile ? 'column-reverse' : 'row',
          }}
          columns={[
            {
              imageSrc: peopleInRoomImage,
              imageAlt: 'people in the room',
              imageStyle: {
                  ...imageStyle,
                  marginRight: 'auto',
                },
            },
            {
              markdown: `## De Voordelen van Samen Zelfbouwen als CPO\n
#### 🫶 Wonen zoals jij dat wilt\n
Je hebt de vrijheid om veel naar eigen wensen te ontwikkelen en te bouwen.\n
#### 👍 Alles bij de hand\n
Met elkaar kun je bedenken welke voorzieningen je belangrijk vindt.\n
#### 🙌 Samen kan je meer\n
Samen sta je sterker. Je profiteert van elkaars kwaliteiten en kunt voorzieningen met elkaar delen.\n
#### 🫰Regie in eigen hand\n
Vrijheid om het te doen zoals jij het wilt en financiële ruimte omdat zelf bouwen goedkoper kan zijn dan het uitbesteden.\n
          `,
            },

          ]}
        />

        <ColumnsSection
          columns={[
            {
              markdown: `
## Hoe kan Rabobank helpen...

##### Rabobank helpt financieel bij de opstart van een CPO
Voordat CPO-plannen definitief zijn, moet er al veel tijd en vaak ook geld geïnvesteerd worden. Speciaal voor klanten die samen een collectief
woonproject (CPO) willen realiseren biedt Rabobank in heel Nederland financiële ondersteuning vanuit de Rabo Ontwikkelbijdrage voor collectieve
woonvormen; een tegemoetkoming van maximaal 5.000 euro ter compensatie van de opstartkosten.

##### Deskundig advies via de Rabobank CPO-desk
De CPO-desk is hét aanspreekpunt voor alle CPO-projecten in jouw eigen regio. Of je nu in de opstartfase zit of al op weg bent. De gespecialiseerde
adviseurs van de Rabobank, zetten hun kennis, kunde én netwerk in om vragen rondom Samen Zelfbouwen te beantwoorden. Ze wijzen de weg,
geven advies én kunnen uiteraard helpen bij de financiering als het zover is.
              `.trim(),
              content: (
                <Title.H5 style={{ marginTop: '1rem', marginBottom: 0 }}>
                  <Text.Medium>
                    Meer weten? Bekijk de Samen Zelfbouwen pagina van de
                  </Text.Medium>
                  {' '}
                  <TextLink
                    to="https://www.rabobank.nl/particulieren/hypotheek/samen-zelfbouwen"
                    externalURL
                    target="_blank"
                    textStyle={{ color: 'var(--primary-blue)' }}
                  >
                    Rabobank.
                  </TextLink>
                </Title.H5>
              ),
            },
          ]}
        />

        <CpoDesks />

      </PageLayout>
    </>
  )
}